<template>
  <InstructorLayout>
    <div class="notification-settings-page">
      <div class="page-header">
        <h1>🔔 LINE通知設定</h1>
        <p>各生徒のフィードバック通知先を設定できます</p>
        <button @click="goBack" class="btn btn-secondary">
          ← 講師管理画面に戻る
        </button>
      </div>

      <div class="settings-description">
        <div class="info-card">
          <h3>📋 設定について</h3>
          <ul>
            <li><strong>学習者のみ</strong>: 学習者のLINEにのみ通知（推奨・デフォルト）</li>
            <li><strong>保護者のみ</strong>: 保護者のLINEにのみ通知（小さなお子様向け）</li>
            <li><strong>両方</strong>: 保護者と学習者の両方のLINEに通知</li>
          </ul>
          <p class="note">💡 基本は学習者のみに通知されます。必要に応じて個別に設定を変更してください。</p>
        </div>
      </div>

      <div class="students-settings">
        <h2>👥 生徒別通知設定</h2>
        
        <div v-if="isLoading" class="loading">
          <p>設定を読み込み中...</p>
        </div>

        <div v-else-if="studentsData.length === 0" class="no-data">
          <p>生徒データがありません</p>
        </div>

        <div v-else class="students-list">
          <div 
            v-for="student in studentsData" 
            :key="student.user_id" 
            class="student-settings-item"
          >
            <div class="student-header">
              <h3>{{ student.student_name }}</h3>
              <span class="current-setting">
                現在: {{ getNotificationTargetText(student.user_id) }}
              </span>
            </div>
            
            <div class="notification-options">
              <label class="radio-option">
                <input 
                  type="radio" 
                  :name="`notification_${student.user_id}`"
                  value="learner_only"
                  :checked="getNotificationTarget(student.user_id) === 'learner_only'"
                  @change="updateNotificationSetting(student.user_id, 'learner_only')"
                >
                <span class="radio-label">
                  <strong>学習者のみ</strong><br>
                  <small>学習者のLINEにのみ通知（推奨）</small>
                </span>
              </label>
              
              <label class="radio-option">
                <input 
                  type="radio" 
                  :name="`notification_${student.user_id}`"
                  value="parent_only"
                  :checked="getNotificationTarget(student.user_id) === 'parent_only'"
                  @change="updateNotificationSetting(student.user_id, 'parent_only')"
                >
                <span class="radio-label">
                  <strong>保護者のみ</strong><br>
                  <small>保護者のLINEにのみ通知（小さなお子様向け）</small>
                </span>
              </label>
              
              <label class="radio-option">
                <input 
                  type="radio" 
                  :name="`notification_${student.user_id}`"
                  value="both"
                  :checked="getNotificationTarget(student.user_id) === 'both'"
                  @change="updateNotificationSetting(student.user_id, 'both')"
                >
                <span class="radio-label">
                  <strong>両方</strong><br>
                  <small>保護者と学習者の両方のLINEに通知</small>
                </span>
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>
  </InstructorLayout>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { supabase } from '../../supabaseClient'
import InstructorLayout from '../../components/layout/InstructorLayout.vue'

const router = useRouter()

// 状態管理
const isLoading = ref(false)
const submissions = ref([])
const notificationSettings = ref({})

// 生徒別データの計算
const studentsData = computed(() => {
  const students = {}
  
  submissions.value.forEach(submission => {
    if (!students[submission.user_id]) {
      students[submission.user_id] = {
        user_id: submission.user_id,
        student_name: submission.student_name || '未設定'
      }
    }
  })
  
  return Object.values(students)
})

onMounted(async () => {
  await loadSubmissions()
  await loadNotificationSettings()
})

// データ読み込み
const loadSubmissions = async () => {
  isLoading.value = true
  
  try {
    const { data, error } = await supabase
      .from('submissions_v3')
      .select('*')
      .order('created_at', { ascending: false })

    if (error) throw error

    submissions.value = data || []
    console.log('✅ 提出課題データ読み込み完了:', data?.length || 0)
  } catch (error) {
    console.error('❌ 提出課題データ読み込みエラー:', error)
    alert('データの読み込みに失敗しました')
  } finally {
    isLoading.value = false
  }
}

// 通知設定を読み込み
const loadNotificationSettings = async () => {
  try {
    // 各生徒の通知設定を取得
    const userIds = [...new Set(submissions.value.map(s => s.user_id))]
    
    for (const userId of userIds) {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'https://api.otoeigo.com'}/api/notification-settings/${userId}`)
      if (response.ok) {
        const result = await response.json()
        if (result.success) {
          notificationSettings.value[userId] = result.data.line_notification_target
        } else {
          notificationSettings.value[userId] = 'learner_only' // デフォルト
        }
      } else {
        notificationSettings.value[userId] = 'learner_only' // デフォルト
      }
    }
    console.log('✅ 通知設定読み込み完了:', notificationSettings.value)
  } catch (error) {
    console.error('通知設定読み込みエラー:', error)
  }
}

// 通知設定を更新
const updateNotificationSetting = async (userId, target) => {
  try {
    const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'https://api.otoeigo.com'}/api/notification-settings/${userId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        line_notification_target: target
      })
    })
    
    if (response.ok) {
      const result = await response.json()
      if (result.success) {
        notificationSettings.value[userId] = target
        console.log('✅ 通知設定更新完了:', { userId, target })
        alert(`${getStudentName(userId)}の通知設定を更新しました！`)
      }
    } else {
      throw new Error('通知設定の更新に失敗しました')
    }
  } catch (error) {
    console.error('通知設定更新エラー:', error)
    alert('通知設定の更新に失敗しました: ' + error.message)
  }
}

// 通知設定の現在値を取得
const getNotificationTarget = (userId) => {
  return notificationSettings.value[userId] || 'learner_only'
}

// 通知設定の表示用テキストを取得
const getNotificationTargetText = (userId) => {
  const target = getNotificationTarget(userId)
  switch (target) {
    case 'learner_only':
      return '学習者のみ'
    case 'parent_only':
      return '親のみ'
    case 'both':
      return '両方'
    default:
      return '学習者のみ'
  }
}

// 生徒名を取得
const getStudentName = (userId) => {
  const student = studentsData.value.find(s => s.user_id === userId)
  return student ? student.student_name : '不明な生徒'
}

// 講師管理画面に戻る
const goBack = () => {
  router.push('/instructor/home')
}
</script>

<style scoped>
.notification-settings-page {
  max-width: 1000px;
  margin: 0 auto;
  padding: 2rem;
}

.page-header {
  text-align: center;
  margin-bottom: 3rem;
}

.page-header h1 {
  color: #1f2937;
  margin-bottom: 0.5rem;
  font-size: 2.5rem;
}

.page-header p {
  color: #6b7280;
  font-size: 1.1rem;
  margin-bottom: 1.5rem;
}

.settings-description {
  margin-bottom: 3rem;
}

.info-card {
  background: #f0f9ff;
  border: 1px solid #0ea5e9;
  border-radius: 12px;
  padding: 2rem;
}

.info-card h3 {
  color: #0c4a6e;
  margin-bottom: 1rem;
  font-size: 1.3rem;
}

.info-card ul {
  margin: 1rem 0;
  padding-left: 1.5rem;
}

.info-card li {
  color: #0c4a6e;
  margin-bottom: 0.5rem;
  line-height: 1.5;
}

.info-card .note {
  color: #0c4a6e;
  font-style: italic;
  margin-top: 1rem;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.5);
  border-radius: 8px;
}

.students-settings h2 {
  color: #1f2937;
  margin-bottom: 2rem;
  font-size: 1.8rem;
  text-align: center;
}

.students-list {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.student-settings-item {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.student-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #f3f4f6;
}

.student-header h3 {
  margin: 0;
  color: #1f2937;
  font-size: 1.4rem;
}

.current-setting {
  color: #6b7280;
  font-size: 0.9rem;
  font-weight: 600;
  background: #e5e7eb;
  padding: 0.5rem 1rem;
  border-radius: 20px;
}

.notification-options {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.radio-option {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  cursor: pointer;
  padding: 1rem;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  transition: all 0.2s;
  background: #f9fafb;
}

.radio-option:hover {
  border-color: #667eea;
  background: #f8fafc;
}

.radio-option input[type="radio"] {
  margin-top: 0.25rem;
  width: 1.2rem;
  height: 1.2rem;
}

.radio-label {
  flex: 1;
  line-height: 1.5;
}

.radio-label strong {
  color: #1f2937;
  font-size: 1rem;
}

.radio-label small {
  color: #6b7280;
  font-size: 0.85rem;
}

.loading, .no-data {
  text-align: center;
  padding: 3rem;
  color: #6b7280;
  font-size: 1.1rem;
}

.btn {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-secondary {
  background: #6b7280;
  color: white;
}

.btn-secondary:hover {
  background: #4b5563;
  transform: translateY(-1px);
}

/* スマホ対応 */
@media (max-width: 768px) {
  .notification-settings-page {
    padding: 1rem;
  }
  
  .page-header h1 {
    font-size: 2rem;
  }
  
  .student-header {
    flex-direction: column;
    gap: 1rem;
    align-items: flex-start;
  }
  
  .notification-options {
    gap: 0.75rem;
  }
  
  .radio-option {
    padding: 0.75rem;
  }
  
  .info-card {
    padding: 1.5rem;
  }
}
</style>

